import jsPDF from 'jspdf';
import logo from '../../../assets/img/logo_constructora.png';
import { formatCurrency, formatBankName } from '../../bank-movements/utils/formatters';
import { formatDate, formatDateTime } from '../../../utils/formatUtils';

const BRAND_GREEN = { r: 94, g: 137, b: 79 };
const NEGATIVE_RED = { r: 183, g: 28, b: 28 };
const POSITIVE_GREEN = { r: 21, g: 101, b: 52 };
const TEXT_PRIMARY = { r: 33, g: 33, b: 33 };
const TEXT_MUTED = { r: 100, g: 100, b: 100 };

const loadImageAsDataURL = (imagePath) =>
  fetch(imagePath)
    .then((response) => response.blob())
    .then(
      (blob) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        })
    );

const formatPeriodLabel = (period) => {
  if (!period?.start_date || !period?.end_date) {
    return '';
  }
  return `${formatDate(period.start_date)} al ${formatDate(period.end_date)}`;
};

const drawHeader = async (doc, pageWidth, margin) => {
  const headerTop = 14;
  try {
    const logoData = await loadImageAsDataURL(logo);

    if (logoData) {
      const image = new Image();
      const metrics = await new Promise((resolve) => {
        image.onload = () => resolve({ width: image.width, height: image.height });
        image.onerror = () => resolve(null);
        image.src = logoData;
      });

      if (metrics && metrics.height > 0) {
        const maxHeight = 24;
        const scaledWidth = (metrics.width / metrics.height) * maxHeight;
        doc.addImage(logoData, 'PNG', margin, headerTop - 6, scaledWidth, maxHeight);
      } else {
        doc.addImage(logoData, 'PNG', margin, headerTop - 6, 60, 20);
      }
    }
  } catch (error) {
    console.warn('No se pudo cargar el logo para el PDF:', error);
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.setTextColor(BRAND_GREEN.r, BRAND_GREEN.g, BRAND_GREEN.b);
  doc.text('Constructora Colonial', pageWidth - margin, headerTop + 4, { align: 'right' });

  doc.setFontSize(13);
  doc.setTextColor(TEXT_PRIMARY.r, TEXT_PRIMARY.g, TEXT_PRIMARY.b);
  doc.text('Finanzas | Informe proyectado', pageWidth - margin, headerTop + 16, { align: 'right' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(TEXT_MUTED.r, TEXT_MUTED.g, TEXT_MUTED.b);
  doc.text(`Generado: ${formatDateTime(new Date())}`, pageWidth - margin, headerTop + 24, {
    align: 'right',
  });

  doc.setDrawColor(BRAND_GREEN.r, BRAND_GREEN.g, BRAND_GREEN.b);
  doc.setLineWidth(1);
  doc.line(margin, headerTop + 28, pageWidth - margin, headerTop + 28);
};

export const generateProjectedReportPdf = async ({ report, bankColumns, formatBankDisplay = formatBankName }) => {
  if (!report || !report.periods?.length) {
    throw new Error('No hay datos para exportar');
  }

  const doc = new jsPDF('landscape');
  const margin = 18;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  await drawHeader(doc, pageWidth, margin);

  const columns = ['Periodo', ...bankColumns.map((bank) => formatBankDisplay(bank)), 'Total neto'];
  const availableWidth = pageWidth - margin * 2;
  const periodWidth = Math.max(48, availableWidth * 0.15);
  const remainingWidth = availableWidth - periodWidth;
  const otherColumns = Math.max(columns.length - 1, 1);
  const dataColumnWidth = remainingWidth / otherColumns;
  const columnWidths = [periodWidth, ...Array(columns.length - 1).fill(dataColumnWidth)];

  let tableY = 60; // start below header

  const renderRow = (
    cells,
    { isHeader = false, skipPageBreak = false, zebra = false } = {}
  ) => {
    const cellPayload = cells.map((cell, index) => {
      const width = columnWidths[index];
      const content = typeof cell === 'string' ? { text: cell } : cell;
      const lines = doc.splitTextToSize(content.text || '', width - 6);
      const baseHeight = isHeader ? 18 : 20;
      const height = Math.max(baseHeight, lines.length * 6 + 6);
      return { width, lines, height, ...content };
    });

    const rowHeight = Math.max(...cellPayload.map((item) => item.height));

    if (!skipPageBreak && tableY + rowHeight > pageHeight - margin) {
      doc.addPage();
      tableY = margin;
      renderRow(columns, { isHeader: true, skipPageBreak: true });
    }

    let currentX = margin;
    cellPayload.forEach((cell) => {
      if (isHeader) {
        doc.setFillColor(BRAND_GREEN.r, BRAND_GREEN.g, BRAND_GREEN.b);
        doc.setDrawColor(BRAND_GREEN.r, BRAND_GREEN.g, BRAND_GREEN.b);
        doc.rect(currentX, tableY, cell.width, rowHeight, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
      } else {
        const fillShade = zebra ? 250 : 255;
        doc.setFillColor(fillShade, fillShade, fillShade);
        doc.setDrawColor(220, 220, 220);
        doc.rect(currentX, tableY, cell.width, rowHeight, 'FD');
        const color = cell.textColor || TEXT_PRIMARY;
        doc.setTextColor(color.r, color.g, color.b);
        doc.setFont('helvetica', cell.fontStyle || 'normal');
      }

      doc.text(cell.lines, currentX + 3, tableY + 8);
      currentX += cell.width;
    });

    tableY += rowHeight;
  };

  renderRow(columns, { isHeader: true, skipPageBreak: true });

  report.periods.forEach((period, index) => {
    const periodCell = formatPeriodLabel(period);

    const bankCells = bankColumns.map((bankName) => {
      const bankData = period.banks?.find((bank) => bank.bank_name === bankName);
      const closing = formatCurrency(bankData?.closing_balance ?? 0, 2);
      const netMovement = bankData?.net_movement ?? 0;
      return {
        text: `${closing}\nNeto: ${formatCurrency(netMovement, 2)}`,
        textColor: netMovement >= 0 ? POSITIVE_GREEN : NEGATIVE_RED,
      };
    });

    const totalNetMovement = period.totals?.net_movement ?? 0;
    const totalCell = {
      text: `${formatCurrency(period.totals?.closing_balance ?? 0, 2)}\nNeto: ${formatCurrency(totalNetMovement, 2)}`,
      textColor: totalNetMovement >= 0 ? POSITIVE_GREEN : NEGATIVE_RED,
    };

    renderRow([periodCell, ...bankCells, totalCell], {
      zebra: index % 2 === 0,
    });
  });

  if (report.account_ids_considered?.length) {
    if (tableY + 18 > pageHeight - margin) {
      doc.addPage();
      tableY = margin;
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(TEXT_PRIMARY.r, TEXT_PRIMARY.g, TEXT_PRIMARY.b);
    doc.text(
      `Cuentas consideradas: ${report.account_ids_considered.length}`,
      margin,
      tableY + 12
    );
    doc.setFont('helvetica', 'normal');
    const bankSummary = bankColumns.map((bank) => formatBankDisplay(bank)).join(', ');
    doc.text(
      `Bancos incluidos: ${bankSummary}`,
      margin,
      tableY + 20
    );
  }

  doc.save('informe_proyectado.pdf');
};
